import { useEffect, useRef, useState } from 'react'
import { BrandMark, DeviceIcon, GiftIcon, GridIcon, LabelIcon, PlayIcon } from './Icons'
import { resetHelpTips } from './InlineHelp'
import { missionCopy } from '../missionCopy'
import { useI18n } from '../../i18n'
import { trackEvent } from '../../analytics'
import { APP_VERSION } from '../../domain/appVersion'

interface Props {
  open: boolean
  returning?: boolean
  onStart: () => void
  onExplore: () => void
}

export const OnboardingDialog = ({ open, returning = false, onStart, onExplore }: Props) => {
  const { language, setLanguage, copy: { onboarding: c } } = useI18n()
  const m = missionCopy[language]
  const [tipsReset, setTipsReset] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [feedback, setFeedback] = useState('')
  const [feedbackStatus, setFeedbackStatus] = useState<string | null>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (open && dialog && !dialog.open) dialog.showModal()
    if (!open && dialog?.open) dialog.close()
    if (open) { setFeedbackStatus(null); setTipsReset(false) }
  }, [open])

  const shareFeedback = async () => {
    const message = feedback.trim()
    if (!message) return
    const text = `${c.commentPrefix}\n\n${message}\n\nLeveleria ${APP_VERSION} · ${language.toLocaleUpperCase()}`
    trackEvent('feedback_opened', language)
    try { await navigator.clipboard?.writeText(text) } catch { /* Email remains the primary route. */ }
    window.location.href = `mailto:hello@leveleria.com?subject=${encodeURIComponent(c.commentTitle)}&body=${encodeURIComponent(text)}`
    setFeedbackStatus(c.feedbackShared)
  }

  if (!open) return null

  return (
    <dialog ref={dialogRef} className="onboarding-dialog" onCancel={(event) => { event.preventDefault(); onExplore() }}>
      <div className="onboarding-brand"><BrandMark /><strong>Level<em>eria</em></strong><span>{c.beta}</span></div>
      <div className="language-switch" role="group" aria-label={language === 'es' ? 'Idioma' : 'Language'}>
        <button type="button" className={language === 'en' ? 'language-switch__button language-switch__button--selected' : 'language-switch__button'} aria-pressed={language === 'en'} onClick={() => setLanguage('en')}>EN</button>
        <button type="button" className={language === 'es' ? 'language-switch__button language-switch__button--selected' : 'language-switch__button'} aria-pressed={language === 'es'} onClick={() => setLanguage('es')}>ES</button>
      </div>
      <p className="eyebrow">{returning ? c.quickGuide : c.welcome}</p>
      <h1>{returning ? c.howWorks : c.title}</h1>
      <p className="onboarding-intro">{c.intro}</p>

      <ol className="onboarding-steps">
        <li><span><GridIcon /></span><div><strong>{c.step1}</strong><p>{c.step1Help}</p></div></li>
        <li className="onboarding-specialization-step">
          <span><LabelIcon /></span>
          <div>
            <strong>{c.step2}</strong>
            <p>{c.step2Help}</p>
            <div className="onboarding-level-demo" aria-label={language === 'es' ? 'Ejemplo de niveles independientes' : 'Independent levels example'}>
              <div className="onboarding-level-demo__reward"><PlayIcon /><b>{c.squats}</b><em>+20 XP</em></div>
              <div className="onboarding-level-demo__row">
                <div><b>{c.fitness}</b><small>{c.areaLevel}</small></div>
                <span><i style={{ width: '68%' }} /></span>
              </div>
              <div className="onboarding-level-demo__row onboarding-level-demo__row--specialization">
                <div><b>{c.strength}</b><small>{c.skillLevel}</small></div>
                <span><i style={{ width: '42%' }} /></span>
              </div>
              <p>{c.independent}</p>
            </div>
          </div>
        </li>
        <li><span><PlayIcon /></span><div><strong>{c.step3}</strong><p>{c.step3Help}</p></div></li>
        <li><span><GiftIcon /></span><div><strong>{c.step4}</strong><p>{c.step4Help}</p></div></li>
        <li><span><GridIcon /></span><div><strong>{m.guideMission}</strong><p>{m.guideMissionHelp}</p></div></li>
        <li><span><PlayIcon /></span><div><strong>{m.guideCheckpoint}</strong><p>{m.guideCheckpointHelp}</p></div></li>
      </ol>
      <section className="onboarding-tips"><h2>{m.guideTips}</h2><p>{m.guideTipsHelp}</p><button className="button button--outline" type="button" onClick={() => { resetHelpTips(); setTipsReset(true) }}>{m.resetHelp}</button>{tipsReset && <p role="status">{m.helpReset}</p>}</section>

      <section className="onboarding-more" aria-label={language === 'es' ? 'Más información' : 'More information'}>
        <details>
          <summary>{c.install}</summary>
          <div>
            <p><strong>Android:</strong> {c.android}</p>
            <p><strong>iPhone:</strong> {c.iphone}</p>
            <p><strong>{language === 'es' ? 'Computadora' : 'Computer'}:</strong> {c.computer}</p>
          </div>
        </details>
        <details>
          <summary>{c.roadmap}</summary>
          <div className="onboarding-roadmap">
            <p><strong>{c.now}</strong> {c.nowText}</p>
            <p><strong>{c.next}</strong> {c.nextText}</p>
            <p><strong>{c.exploring}</strong> {c.exploringText}</p>
          </div>
        </details>
        <details>
          <summary>{c.feedback}</summary>
          <div className="onboarding-feedback">
            <label htmlFor={returning ? 'help-feedback' : 'welcome-feedback'}>{c.feedbackPrompt}</label>
            <textarea id={returning ? 'help-feedback' : 'welcome-feedback'} value={feedback} maxLength={600} onChange={(event) => { setFeedback(event.target.value); setFeedbackStatus(null) }} placeholder={c.feedbackPlaceholder} />
            <button className="button button--outline" type="button" disabled={!feedback.trim()} onClick={() => void shareFeedback()}>{c.shareFeedback}</button>
            {feedbackStatus && <p className="onboarding-feedback__status" role="status">{feedbackStatus}</p>}
          </div>
        </details>
        <details>
          <summary>{c.contact}</summary>
          <div><p>{c.contactHelp} <a href="mailto:hello@leveleria.com" onClick={() => trackEvent('feedback_opened', language)}>hello@leveleria.com</a></p></div>
        </details>
      </section>

      <div className="onboarding-local"><DeviceIcon /><span>{c.local}</span></div>
      <button className="button button--primary" type="button" onClick={onStart}>{returning ? c.back : c.tryNow}</button>
      {!returning && <button className="onboarding-skip" type="button" onClick={onExplore}>{c.explore}</button>}
    </dialog>
  )
}
